// ============================================================================
// JARVISTRAVEL - INVITATION LINK
// The page an invitation's accept or decline link opens. It answers once, as
// the signed-in person, then opens the trip or says what happened.
// ============================================================================

import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { AlertTriangle, Check, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/app/components/shared';
import { Paywall } from '@/app/components/subscription/Paywall';
import { answerInvitation, type CollaboratorData, type InvitationAnswer } from '@/app/api/collaborators';
import { listTrips } from '@/app/api/trips';
import { isDemoSession } from '@/app/api/client';
import { SIGN_IN_PATH } from '@/app/components/auth/authPaths';
import { clearPlanIntent } from '@/app/utils/planIntent';
import { forgetReturnTo, saveReturnTo } from '@/app/utils/returnTo';

type Outcome =
  | { kind: 'answering' }
  | { kind: 'declined' }
  | { kind: 'refused'; reason: 'other-account' | 'answered' | 'not-open' }
  | { kind: 'no-room'; reason: string }
  | { kind: 'failed' };

// By core's code, not the status alone: a 403 also means a new account core
// hasn't finished setting up, which is worth retrying, not someone else's.
// Read by shape, as isTripLocked does, so an error from another copy of the
// client module is still read.
function outcomeOf(error: unknown): Outcome {
  const refusal = error as { detailCode?: string; message?: string } | null;
  switch (refusal?.detailCode) {
    case 'FORBIDDEN':
      return { kind: 'refused', reason: 'other-account' };
    // CONFLICT: another invitation to this trip is already theirs, and one is
    // only ever bound to a person by answering it.
    case 'ALREADY_RESPONDED':
    case 'CONFLICT':
      return { kind: 'refused', reason: 'answered' };
    case 'NOT_FOUND':
    case 'INVALID_COLLABORATOR_ID':
    case 'INVALID_TRIP_ID':
      return { kind: 'refused', reason: 'not-open' };
    // A trip shared with them counts toward their plan, which has no room for
    // it. Core's message says why, in the plan's words.
    case 'SUBSCRIPTION_REQUIRED':
      return { kind: 'no-room', reason: refusal?.message ?? '' };
    default:
      return { kind: 'failed' };
  }
}

const REFUSED = {
  'other-account': {
    title: 'This invitation is for another account',
    body: 'It was sent to a different email address. Sign in with that address to answer it.',
  },
  answered: {
    title: 'Already answered',
    body: 'You’ve already answered an invitation to this trip, so this link can’t be used again.',
  },
  'not-open': {
    title: 'This invitation isn’t open',
    body: 'The owner may have withdrawn it. Ask them to invite you again.',
  },
} as const;

interface InvitationLinkProps {
  answer: InvitationAnswer;
  /** Signs out, and keeps this link for the account it was sent to. */
  onSwitchAccount?: (path: string) => void;
}

export function InvitationLink(props: InvitationLinkProps) {
  const { tripId = '', collaboratorId = '' } = useParams();
  // A fresh page for each invitation and answer. React keeps one instance
  // across routes that render the same component, and nothing may carry over,
  // least of all a decline's confirmation.
  return <InvitationPage key={`${tripId}/${collaboratorId}/${props.answer}`} {...props} />;
}

// The return type makes a new Outcome with no case below a build error, not a
// blank page.
function InvitationPage({ answer, onSwitchAccount }: InvitationLinkProps): JSX.Element {
  const { tripId = '', collaboratorId = '' } = useParams();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'answering' });
  const [attempt, setAttempt] = useState(0);
  const [choosingPlan, setChoosingPlan] = useState(false);
  // A decline can't be undone, so it waits for a tap. Following the link, a
  // reload or a link preview must not refuse an invitation.
  const [confirmed, setConfirmed] = useState(answer === 'accept');
  // Someone turning a trip down didn't come to buy a plan, so a plan chosen
  // earlier in this tab is dropped: no checkout before the decline or after it.
  useEffect(() => {
    if (answer === 'decline') clearPlanIntent();
  }, [answer]);
  // Once the invitation is settled, a path stashed to bring them back to it
  // has nothing left to do. Left, a later sign-in in this tab would land on
  // "Already answered". A plan being chosen stashes it again.
  useEffect(() => {
    if (outcome.kind === 'declined' || outcome.kind === 'refused' || outcome.kind === 'no-room') forgetReturnTo(pathname);
  }, [outcome.kind, pathname]);
  // One request per attempt. StrictMode runs the effect twice, and a second
  // request would be refused as already answered.
  const request = useRef<{ key: string; answered: Promise<CollaboratorData> } | null>(null);
  const demo = isDemoSession();

  useEffect(() => {
    // Signed out, or with a task still to finish, App's routing takes the
    // person to sign in or to the task, and the link waits for them.
    if (demo || isSignedIn !== true || !confirmed) return;
    const key = `${tripId}/${collaboratorId}/${answer}/${attempt}`;
    if (request.current?.key !== key) {
      request.current = { key, answered: answerInvitation(tripId, collaboratorId, answer) };
    }
    let live = true;
    request.current.answered.then(
      (collaborator) => {
        if (!live) return;
        if (answer === 'accept') {
          forgetReturnTo(pathname);
          navigate(`/trips/${collaborator.trip_id}`, { replace: true });
        } else setOutcome({ kind: 'declined' });
      },
      (error: unknown) => {
        if (!live) return;
        const refused = outcomeOf(error);
        // An accept whose earlier try went through, and whose answer was lost,
        // now reads as answered. If the trip is in their list, they joined it.
        if (answer === 'accept' && refused.kind === 'refused' && refused.reason === 'answered') {
          listTrips().then(
            (trips) => {
              if (!live) return;
              if (trips.some((trip) => trip.id === tripId)) navigate(`/trips/${tripId}`, { replace: true });
              else setOutcome(refused);
            },
            () => {
              if (live) setOutcome(refused);
            },
          );
          return;
        }
        setOutcome(refused);
      },
    );
    return () => {
      live = false;
    };
  }, [demo, isSignedIn, confirmed, tripId, collaboratorId, answer, attempt, navigate, pathname]);

  const toTrips = (variant: 'primary' | 'ghost') => (
    <Button variant={variant} fullWidth onClick={() => navigate('/trips', { replace: true })}>
      Go to your trips
    </Button>
  );

  if (demo) {
    const signIn = () => {
      saveReturnTo(pathname);
      navigate(SIGN_IN_PATH);
    };
    return (
      <Page
        icon={<AlertTriangle className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />}
        title="Sign in to answer this invitation"
        body="The demo isn’t your account, so it can’t join a trip."
      >
        <Button variant="primary" fullWidth onClick={signIn}>Sign in</Button>
      </Page>
    );
  }

  if (!confirmed) {
    return (
      <Page
        icon={<AlertTriangle className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />}
        title="Decline this invitation?"
        body="You won’t be added to this trip. If you change your mind, ask the owner to invite you again."
      >
        <Button variant="primary" fullWidth onClick={() => setConfirmed(true)}>Decline</Button>
        <Button variant="ghost" fullWidth onClick={() => navigate('/trips', { replace: true })}>Not now</Button>
      </Page>
    );
  }

  switch (outcome.kind) {
    case 'answering':
      return (
        <Page
          icon={<Loader2 className="w-7 h-7 animate-spin" strokeWidth={1.75} aria-hidden="true" />}
          title={answer === 'accept' ? 'Joining the trip' : 'Declining the invitation'}
          body="Just a moment."
        />
      );
    case 'declined':
      return (
        <Page
          icon={<Check className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />}
          title="Invitation declined"
          body="You won’t be added to this trip. If you change your mind, ask the owner to invite you again."
        >
          {toTrips('primary')}
        </Page>
      );
    case 'refused': {
      const canSwitch = outcome.reason === 'other-account' && onSwitchAccount;
      return (
        <Page icon={<AlertTriangle className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />} {...REFUSED[outcome.reason]}>
          {canSwitch && (
            <Button variant="primary" fullWidth onClick={() => onSwitchAccount(pathname)}>Use another account</Button>
          )}
          {toTrips(canSwitch ? 'ghost' : 'primary')}
        </Page>
      );
    }
    case 'no-room': {
      // The link is kept, so the checkout's return brings them back to answer.
      const choosePlan = () => {
        saveReturnTo(pathname);
        setChoosingPlan(true);
      };
      return (
        <Page
          icon={<Sparkles className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />}
          title="Choose a plan to join this trip"
          body={outcome.reason || 'A trip shared with you counts toward your plan.'}
        >
          <Button variant="primary" fullWidth onClick={choosePlan}>Choose a plan</Button>
          {toTrips('ghost')}
          <Paywall
            isOpen={choosingPlan}
            onClose={() => {
              setChoosingPlan(false);
              forgetReturnTo(pathname);
            }}
            title="Choose a plan"
            intro="A trip shared with you counts toward your plan, as one you plan yourself does. After checkout, you’ll come back to this invitation."
          />
        </Page>
      );
    }
    case 'failed': {
      const retry = () => {
        setOutcome({ kind: 'answering' });
        setAttempt((n) => n + 1);
      };
      return (
        <Page
          icon={<AlertTriangle className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />}
          tone="danger"
          title="We couldn’t answer the invitation"
          body="Try again in a moment."
        >
          <Button variant="primary" fullWidth onClick={retry}>Try again</Button>
          {toTrips('ghost')}
        </Page>
      );
    }
  }
}

function Page({
  icon,
  title,
  body,
  tone = 'neutral',
  children,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  tone?: 'neutral' | 'danger';
  children?: ReactNode;
}) {
  return (
    <div className="min-h-dvh flex items-center justify-center px-6 py-12 bg-bg">
      <div className="max-w-sm w-full text-center">
        <div
          className={`mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${
            tone === 'danger' ? 'bg-danger/10 text-danger' : 'bg-surface-raised text-ink'
          }`}
        >
          {icon}
        </div>
        {/* A live region, so the change from "joining" to what happened is
            announced. The buttons stay outside it. */}
        <div role="status">
          <h1 className="text-xl font-bold text-ink mb-1.5">{title}</h1>
          <p className="text-sm text-muted mb-6">{body}</p>
        </div>
        {children && <div className="flex flex-col gap-2">{children}</div>}
      </div>
    </div>
  );
}
